import React, { useCallback } from "react";
import { Text, View, Link } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import { handleClick } from "lib/utils/navigator";

export const TemplateTwoPDFSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  return (
  <View
    style={{
      ...styles.flexCol,
      gap: spacing["2"],
      marginTop: spacing["5"],
      ...style,
    }}
  >
    {heading && (
      <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
        <Text
          style={{
            fontWeight: "bold",
            letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
            color: themeColor || DEFAULT_FONT_COLOR,
          }}
          debug={DEBUG_RESUME_PDF_FLAG}
        >
          {heading}
        </Text>
      </View>
    )}
    {children}
  </View>
)};

// Enhanced version with moderate spacing optimization and orphan heading prevention (following Template Two pattern)
export const TemplateThreeEnhancedSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  // Group content to prevent orphan headings 
  const groupedChildren = React.Children.toArray(children);
  
  return (
    <View
      style={{
        ...styles.flexCol,
        gap: "9pt", //  spacing["2"] (24pt) 
        marginTop: "30pt", //  spacing["5"] (60pt)
        ...style,
      }}
    >
      <View wrap={false}> {/* Prevent heading orphans */}
        {heading && (
          <View style={{ ...styles.flexCol, gap: "6pt" }}>
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: themeColor || DEFAULT_FONT_COLOR,
                lineHeight: "1.3",
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
          </View>
        )}
        {/* First item stays with heading to prevent orphans */}
        {groupedChildren.length > 0 && (
          <View wrap={false}>
            {groupedChildren[0]}
          </View>
        )}
      </View>
      {/* Remaining items */}
      {groupedChildren.slice(1).map((child, index) => (
        <View key={index + 1} wrap={false}>
          {child}
        </View>
      ))}
    </View>
  );
};

export const TemplateThreeProfileSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => (
  <View
    style={{
      ...styles.flexCol,
      gap: spacing["2"],
      paddingTop: spacing["5"],
      ...style,
      backgroundColor: themeColor,
      marginTop: "-1.1pt",
    }}
  >
    {heading && (
      <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
        <Text
          style={{
            fontWeight: "bold",
            letterSpacing: "0.3pt", 
            color: themeColor || DEFAULT_FONT_COLOR,
          }}
          debug={DEBUG_RESUME_PDF_FLAG}
        >
          {heading}
        </Text>
      </View>
    )}
    {children}
  </View>
);

// Enhanced profile section with moderate spacing optimization
export const TemplateThreeEnhancedProfileSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => (
  <View
    style={{
      ...styles.flexCol,
      gap: spacing["1.5"], // Moderate reduction: 2 -> 1.5
      paddingTop: spacing["3"], // Moderate reduction: 5 -> 3
      ...style,
      backgroundColor: themeColor,
      marginTop: "-1.1pt",
    }}
  >
    {heading && (
      <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
        <Text
          style={{
            fontWeight: "bold",
            letterSpacing: "0.3pt",
            color: themeColor || DEFAULT_FONT_COLOR,
          }}
          debug={DEBUG_RESUME_PDF_FLAG}
        >
          {heading}
        </Text>
      </View>
    )}
    {children}
  </View>
);

// Enhanced version with optimized spacing and orphan heading prevention (TemplateThree specific)
export const TemplateThreePDFSectionWithSubsections = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  // Group content to prevent orphan headings
  const groupedChildren = React.Children.toArray(children);
  const firstChild = groupedChildren[0];
  const restChildren = groupedChildren.slice(1);
  
  return (
    <div
      onClick={handleClickCallback}
      style={{
        ...styles.flexCol,
        gap: "9pt", // Optimized spacing
        marginTop: "30pt", // Optimized spacing
        cursor: "pointer"
      }}
    >
      {/* Wrap heading with first child only to prevent orphan headings */}
      {heading && firstChild && (
        <View
          style={{
            ...styles.flexCol,
            gap: "9pt",
          }}
          wrap={false}
        >
          {/* Heading */}
          <View style={{ ...styles.flexCol, gap: "6pt" }}>
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: themeColor || DEFAULT_FONT_COLOR,
                lineHeight: "1.3",
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
          </View>
          {/* First child inside the same container */}
          {firstChild}
        </View>
      )}
      
      {/* Heading only (when no children) */}
      {heading && !firstChild && (
        <View style={{ ...styles.flexCol, gap: "6pt" }}>
          <Text
            style={{
              fontWeight: "bold",
              letterSpacing: "0.3pt",
              color: themeColor || DEFAULT_FONT_COLOR,
              lineHeight: "1.3",
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
        </View>
      )}
      
      {/* Rest of children - can break across pages */}
      {restChildren.length > 0 && (
        <View style={{ ...styles.flexCol, gap: "9pt" }}>
          {restChildren}
        </View>
      )}
    </div>
  );
};
